import EcoOutlinedIcon from '@mui/icons-material/EnergySavingsLeaf';
import MonetizationOnOutlinedIcon from '@mui/icons-material/MonetizationOnOutlined';
import NotInterestedIcon from '@mui/icons-material/NotInterested';
import { SxProps, Tooltip } from '@mui/material';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { FC } from 'react';
import { useIntl } from 'react-intl';
import { ListType } from 'types/apollo/globalTypes';
import messages from './messages';

interface LeadListTypeIconProps {
  listType: ListType | number;
  withLabel?: boolean;
  sx?: SxProps;
  tooltip?: boolean | string;
}

export const LeadListTypeIcon: FC<LeadListTypeIconProps> = ({
  listType,
  withLabel = false,
  sx,
  tooltip = false,
}) => {
  const { formatMessage } = useIntl();
  const theme = useTheme();

  let icon;
  let label: {
    id: string;
    defaultMessage: string;
  };
  const resolvedType = typeof listType === 'number' ? getListTypeNameByValue(listType) : listType;

  const colorSx = () => {
    switch (resolvedType) {
      case ListType.DNC:
        return { color: theme.palette.error.main };
      case ListType.ENDCONTACT:
        return { color: theme.palette.warning.main };
      case ListType.CLOSE:
        return { color: '#FFB81C' };
      case ListType.PROSPECTIVE:
      default:
        return { color: theme.palette.success.main };
    }
  };

  const color = colorSx();

  switch (typeof listType === 'number' ? getListTypeNameByValue(listType) : listType) {
    case ListType.DNC:
      icon = <NotInterestedIcon sx={{ ...color, ...sx }} />;
      label = messages.dnc;
      break;
    case ListType.ENDCONTACT:
      icon = <NotInterestedIcon sx={{ ...color, ...sx }} />;
      label = messages.endContact;
      break;
    case ListType.CLOSE:
      icon = <MonetizationOnOutlinedIcon sx={{ ...color, ...sx }} />;
      label = messages.close;
      break;
    case ListType.PROSPECTIVE:
    default:
      icon = <EcoOutlinedIcon sx={{ ...color, ...sx }} />;
      label = messages.prospective;
  }
  const title = typeof tooltip === 'string' ? tooltip : formatMessage(label);

  const content = (
    <Box
      display="flex"
      alignItems="center"
      component="span"
      sx={{ lineHeight: 0 }}
      onClick={(e) => e.stopPropagation()}
    >
      {icon}
      {withLabel && (
        <Typography sx={(theme) => ({ ...color, ml: theme.spacing(1) })}>
          {formatMessage(label)}
        </Typography>
      )}
    </Box>
  );

  return tooltip ? (
    <Tooltip title={title} arrow>
      <span aria-label={title}>{content}</span>
    </Tooltip>
  ) : (
    content
  );
};

export const getListTypeValueByName = (listType: ListType): number => {
  switch (listType) {
    case ListType.PROSPECTIVE:
      return 0;
    case ListType.DNC:
      return 1;
    case ListType.CLOSE:
      return 2;
    case ListType.ENDCONTACT:
      return 3;
  }
};

export const getListTypeNameByValue = (listType: number): ListType | null => {
  switch (listType) {
    case 0:
      return ListType.PROSPECTIVE;
    case 1:
      return ListType.DNC;
    case 2:
      return ListType.CLOSE;
    case 3:
      return ListType.ENDCONTACT;
  }
  return null;
};
